<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="건의/신고"
        subtitle="서비스 개선 및 신고 접수"
        icon="leads"
        iconColor="blue"
      >
        <template #actions>
          <button
            @click="showWriteModal = true"
            class="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            새 건의/신고
          </button>
        </template>
      </PartnersHeader>
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 왼쪽: 필터 & 요약 -->
          <div class="lg:col-span-1 space-y-4">
            <!-- 필터 카드 -->
            <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <h3 class="text-sm font-semibold text-slate-500 mb-4">상태 필터</h3>
              <div class="space-y-2">
                <button
                  @click="filterStatus = null"
                  :class="[
                    'w-full flex items-center justify-between px-4 py-3 rounded-xl transition',
                    filterStatus === null
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                  ]"
                >
                  <span class="font-medium">전체</span>
                  <span :class="[
                    'px-2.5 py-0.5 rounded-full text-xs font-semibold',
                    filterStatus === null
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-200 text-slate-600'
                  ]">
                    {{ feedbacks.length }}
                  </span>
                </button>
                <button
                  v-for="(statusInfo, key) in FEEDBACK_STATUS"
                  :key="key"
                  @click="filterStatus = key"
                  :class="[
                    'w-full flex items-center justify-between px-4 py-3 rounded-xl transition',
                    filterStatus === key
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                  ]"
                >
                  <span class="font-medium">{{ statusInfo.label }}</span>
                  <span :class="[
                    'px-2.5 py-0.5 rounded-full text-xs font-semibold',
                    filterStatus === key
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-200 text-slate-600'
                  ]">
                    {{ getCountByStatus(key) }}
                  </span>
                </button>
              </div>
            </div>

            <!-- 요약 카드 -->
            <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <h3 class="text-sm font-semibold text-slate-500 mb-4">접수 현황</h3>
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <span class="text-slate-600">전체 접수</span>
                  <span class="font-bold text-slate-900">{{ feedbacks.length }}건</span>
                </div>
                <div class="h-px bg-slate-100"></div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-slate-400"></div>
                    <span class="text-slate-600">접수됨</span>
                  </div>
                  <span class="font-medium text-slate-600">{{ getCountByStatus('pending') }}건</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span class="text-slate-600">처리중</span>
                  </div>
                  <span class="font-medium text-blue-600">{{ getCountByStatus('in_progress') }}건</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-emerald-500"></div>
                    <span class="text-slate-600">처리완료</span>
                  </div>
                  <span class="font-medium text-emerald-600">{{ getCountByStatus('resolved') }}건</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-red-500"></div>
                    <span class="text-slate-600">반려</span>
                  </div>
                  <span class="font-medium text-red-600">{{ getCountByStatus('rejected') }}건</span>
                </div>
              </div>
            </div>

            <!-- 카테고리 필터 -->
            <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <h3 class="text-sm font-semibold text-slate-500 mb-4">카테고리</h3>
              <div class="space-y-2">
                <button
                  @click="filterCategory = null"
                  :class="[
                    'w-full flex items-center justify-between px-4 py-2.5 rounded-xl transition text-sm',
                    filterCategory === null
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                  ]"
                >
                  <span class="font-medium">전체</span>
                </button>
                <button
                  v-for="cat in FEEDBACK_CATEGORIES"
                  :key="cat.id"
                  @click="filterCategory = cat.id"
                  :class="[
                    'w-full flex items-center justify-between px-4 py-2.5 rounded-xl transition text-sm',
                    filterCategory === cat.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                  ]"
                >
                  <span class="font-medium">{{ cat.label }}</span>
                  <span :class="[
                    'px-2 py-0.5 rounded-full text-xs font-semibold',
                    filterCategory === cat.id
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-200 text-slate-600'
                  ]">
                    {{ getCountByCategory(cat.id) }}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- 오른쪽: 목록 -->
          <div class="lg:col-span-2">
            <!-- 로딩 -->
            <div v-if="loading" class="bg-white rounded-2xl p-12 shadow-sm border border-slate-100 text-center">
              <div class="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p class="text-slate-500">목록을 불러오는 중...</p>
            </div>

            <!-- 빈 상태 -->
            <div v-else-if="filteredFeedbacks.length === 0" class="bg-white border border-slate-200 rounded-2xl p-12 text-center">
              <div class="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                </svg>
              </div>
              <p class="text-[15px] font-medium text-slate-500">접수된 건의/신고가 없습니다</p>
              <p class="text-[13px] text-slate-400 mt-1">서비스 개선을 위한 의견을 보내주세요</p>
              <button
                @click="showWriteModal = true"
                class="mt-4 px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition"
              >
                건의/신고 작성하기
              </button>
            </div>

            <!-- 목록 -->
            <div v-else class="space-y-3">
              <div
                v-for="feedback in filteredFeedbacks"
                :key="feedback.id"
                @click="openDetail(feedback)"
                class="bg-white rounded-2xl border border-slate-100 overflow-hidden cursor-pointer hover:border-blue-300 transition"
              >
                <div class="p-4">
                  <div class="flex items-start gap-3">
                    <!-- 상태 아이콘 -->
                    <div
                      :class="[
                        'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                        getStatusIconBg(feedback.status)
                      ]"
                    >
                      <svg v-if="feedback.status === 'pending'" class="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <svg v-else-if="feedback.status === 'in_progress'" class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                      </svg>
                      <svg v-else-if="feedback.status === 'resolved'" class="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                      <svg v-else class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </div>

                    <!-- 내용 -->
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <span class="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                          {{ getCategoryLabel(feedback.category) }}
                        </span>
                        <span
                          :class="[
                            'px-2 py-0.5 text-xs font-medium rounded-full',
                            getStatusBadgeClass(feedback.status)
                          ]"
                        >
                          {{ getStatusLabel(feedback.status) }}
                        </span>
                        <span v-if="feedback.adminReply" class="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          답변완료
                        </span>
                      </div>
                      <h3 class="text-[15px] font-bold text-slate-900 mb-1">{{ feedback.title }}</h3>
                      <p class="text-[13px] text-slate-500 line-clamp-2">{{ feedback.content }}</p>
                      <p class="text-[12px] text-slate-400 mt-2">{{ formatDate(feedback.createdAt) }}</p>
                    </div>

                    <!-- 화살표 -->
                    <svg class="w-5 h-5 text-slate-300 flex-shrink-0 mt-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 작성 모달 -->
    <div
      v-if="showWriteModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      @click.self="showWriteModal = false"
    >
      <div class="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div class="p-6 border-b border-slate-100">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-bold text-slate-900">건의/신고 작성</h2>
            <button
              @click="showWriteModal = false"
              class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition"
            >
              <svg class="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <form @submit.prevent="handleSubmit" class="p-6 space-y-5">
          <!-- 카테고리 선택 -->
          <div>
            <label class="block text-sm font-semibold text-slate-700 mb-2">카테고리</label>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="cat in FEEDBACK_CATEGORIES"
                :key="cat.id"
                type="button"
                @click="form.category = cat.id"
                class="px-4 py-2 rounded-full text-sm font-medium transition border"
                :class="form.category === cat.id
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'"
              >
                {{ cat.label }}
              </button>
            </div>
          </div>

          <!-- 제목 -->
          <div>
            <label class="block text-sm font-semibold text-slate-700 mb-2">제목</label>
            <input
              v-model="form.title"
              type="text"
              placeholder="제목을 입력해주세요"
              class="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition text-sm"
              required
            />
          </div>

          <!-- 내용 -->
          <div>
            <label class="block text-sm font-semibold text-slate-700 mb-2">내용</label>
            <textarea
              v-model="form.content"
              rows="6"
              placeholder="상세 내용을 입력해주세요"
              class="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition resize-none text-sm"
              required
            ></textarea>
          </div>

          <!-- 버튼 -->
          <div class="flex justify-end gap-3 pt-2">
            <button
              type="button"
              @click="showWriteModal = false"
              class="px-5 py-2.5 rounded-xl font-medium text-slate-600 hover:bg-slate-100 transition text-sm"
            >
              취소
            </button>
            <button
              type="submit"
              :disabled="submitting"
              class="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {{ submitting ? '제출 중...' : '제출하기' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 상세 모달 -->
    <div
      v-if="selectedFeedback"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      @click.self="selectedFeedback = null"
    >
      <div class="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <!-- 모달 헤더 -->
        <div class="p-6 border-b border-slate-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                {{ getCategoryLabel(selectedFeedback.category) }}
              </span>
              <span
                :class="[
                  'px-2 py-0.5 text-xs font-medium rounded-full',
                  getStatusBadgeClass(selectedFeedback.status)
                ]"
              >
                {{ getStatusLabel(selectedFeedback.status) }}
              </span>
            </div>
            <button
              @click="selectedFeedback = null"
              class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition"
            >
              <svg class="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <h2 class="text-lg font-bold text-slate-900 mt-3">{{ selectedFeedback.title }}</h2>
          <p class="text-[12px] text-slate-400 mt-1">{{ formatDate(selectedFeedback.createdAt) }}</p>
        </div>

        <!-- 모달 내용 -->
        <div class="p-6">
          <p class="whitespace-pre-wrap text-[14px] text-slate-700 leading-relaxed">{{ selectedFeedback.content }}</p>

          <!-- 관리자 답변 -->
          <div v-if="selectedFeedback.adminReply" class="mt-6 pt-6 border-t border-slate-100">
            <div class="flex items-center gap-2 mb-3">
              <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
              </div>
              <div>
                <p class="text-[14px] font-bold text-slate-900">관리자 답변</p>
                <p class="text-[11px] text-slate-400">{{ formatDate(selectedFeedback.resolvedAt) }}</p>
              </div>
            </div>
            <div class="bg-blue-50 rounded-xl p-4">
              <p class="text-[14px] text-slate-700 whitespace-pre-wrap leading-relaxed">{{ selectedFeedback.adminReply }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'
import { useFeedback, FEEDBACK_CATEGORIES, FEEDBACK_STATUS } from '@/composables/partners/useFeedback'

export default {
  name: 'FeedbackView',
  components: { PartnersLayout, PartnersHeader },
  setup() {
    const { loading, createFeedback, getMyFeedbacks, formatTimeAgo } = useFeedback()

    const filterStatus = ref(null)
    const filterCategory = ref(null)
    const feedbacks = ref([])
    const selectedFeedback = ref(null)
    const showWriteModal = ref(false)
    const submitting = ref(false)

    const form = ref({
      category: 'suggestion',
      title: '',
      content: ''
    })

    const filteredFeedbacks = computed(() => {
      let result = feedbacks.value
      if (filterStatus.value) {
        result = result.filter(f => f.status === filterStatus.value)
      }
      if (filterCategory.value) {
        result = result.filter(f => f.category === filterCategory.value)
      }
      return result
    })

    const getCountByStatus = (status) => {
      return feedbacks.value.filter(f => f.status === status).length
    }

    const getCountByCategory = (category) => {
      return feedbacks.value.filter(f => f.category === category).length
    }

    const loadFeedbacks = async () => {
      try {
        const result = await getMyFeedbacks()
        feedbacks.value = result.feedbacks
      } catch (err) {
        console.error('Failed to load feedbacks:', err)
      }
    }

    const handleSubmit = async () => {
      if (!form.value.title.trim() || !form.value.content.trim()) {
        alert('제목과 내용을 모두 입력해주세요.')
        return
      }

      submitting.value = true
      try {
        await createFeedback(form.value)
        alert('건의/신고가 접수되었습니다.')
        resetForm()
        showWriteModal.value = false
        await loadFeedbacks()
      } catch (err) {
        alert('제출에 실패했습니다: ' + err.message)
      } finally {
        submitting.value = false
      }
    }

    const resetForm = () => {
      form.value = {
        category: 'suggestion',
        title: '',
        content: ''
      }
    }

    const openDetail = (feedback) => {
      selectedFeedback.value = feedback
    }

    const getCategoryLabel = (categoryId) => {
      const cat = FEEDBACK_CATEGORIES.find(c => c.id === categoryId)
      return cat ? cat.label : categoryId
    }

    const getStatusLabel = (status) => {
      return FEEDBACK_STATUS[status]?.label || status
    }

    const getStatusBadgeClass = (status) => {
      const map = {
        pending: 'bg-slate-100 text-slate-600',
        in_progress: 'bg-blue-100 text-blue-700',
        resolved: 'bg-emerald-100 text-emerald-700',
        rejected: 'bg-red-100 text-red-700'
      }
      return map[status] || 'bg-slate-100 text-slate-600'
    }

    const getStatusIconBg = (status) => {
      const map = {
        pending: 'bg-slate-100',
        in_progress: 'bg-blue-100',
        resolved: 'bg-emerald-100',
        rejected: 'bg-red-100'
      }
      return map[status] || 'bg-slate-100'
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
    }

    onMounted(() => {
      loadFeedbacks()
    })

    return {
      loading,
      filterStatus,
      filterCategory,
      feedbacks,
      filteredFeedbacks,
      selectedFeedback,
      showWriteModal,
      form,
      submitting,
      FEEDBACK_CATEGORIES,
      FEEDBACK_STATUS,
      getCountByStatus,
      getCountByCategory,
      handleSubmit,
      resetForm,
      openDetail,
      getCategoryLabel,
      getStatusLabel,
      getStatusBadgeClass,
      getStatusIconBg,
      formatTimeAgo,
      formatDate
    }
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
